import { useState, useEffect } from "react"
import { Repeat } from "lucide-react"
import type { RecurrenceRule } from "@/lib/types"

interface RecurrenceEditorProps {
  value: RecurrenceRule | null
  endDate: string | null
  onChange: (rule: RecurrenceRule | null, endDate: string | null) => void
}

const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Codziennie",
  weekly: "Co tydzień",
  monthly: "Co miesiąc",
  yearly: "Co rok",
}

const DAY_LABELS = ["Nd", "Pn", "Wt", "Śr", "Cz", "Pt", "So"]

export function RecurrenceEditor({ value, endDate, onChange }: RecurrenceEditorProps) {
  const [enabled, setEnabled] = useState(!!value)
  const [frequency, setFrequency] = useState<RecurrenceRule["frequency"]>(value?.frequency || "weekly")
  const [interval, setInterval] = useState(value?.interval || 1)
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(value?.days_of_week || [1]) // Monday default
  const [dayOfMonth, setDayOfMonth] = useState(value?.day_of_month || 1)
  const [localEndDate, setLocalEndDate] = useState(endDate || "")

  useEffect(() => {
    if (!enabled) {
      onChange(null, null)
      return
    }

    const rule: RecurrenceRule = {
      frequency,
      interval,
    }

    if (frequency === "weekly") {
      rule.days_of_week = daysOfWeek
    }
    if (frequency === "monthly") {
      rule.day_of_month = dayOfMonth
    }

    onChange(rule, localEndDate || null)
  }, [enabled, frequency, interval, daysOfWeek, dayOfMonth, localEndDate])

  const toggleDay = (day: number) => {
    setDaysOfWeek(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    )
  }

  const inputClass = "h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none"

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            enabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          }`}
        >
          <Repeat size={14} />
          {enabled ? "Powtarzaj" : "Nie powtarzaj"}
        </button>
      </div>

      {enabled && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Częstotliwość</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as RecurrenceRule["frequency"])}
                className={inputClass + " border-input text-foreground"}
              >
                {Object.entries(FREQUENCY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Co ile</label>
              <input
                type="number"
                min={1}
                max={365}
                value={interval}
                onChange={(e) => setInterval(parseInt(e.target.value) || 1)}
                className={inputClass + " border-input text-foreground"}
              />
            </div>
          </div>

          {frequency === "weekly" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Dni tygodnia</label>
              <div className="flex gap-1">
                {DAY_LABELS.map((label, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium transition-colors ${
                      daysOfWeek.includes(idx) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {frequency === "monthly" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Dzień miesiąca</label>
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value) || 1)}
                className={inputClass + " border-input text-foreground"}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Powtarzaj do (opcjonalnie)</label>
            <input
              type="date"
              value={localEndDate}
              onChange={(e) => setLocalEndDate(e.target.value)}
              className={inputClass + " border-input text-foreground"}
            />
          </div>

          {/* Summary */}
          <p className="text-xs text-muted-foreground">
            {buildSummary(frequency, interval, daysOfWeek, dayOfMonth, localEndDate)}
          </p>
        </div>
      )}
    </div>
  )
}

function buildSummary(
  frequency: string,
  interval: number,
  daysOfWeek: number[],
  dayOfMonth: number,
  endDate: string
): string {
  let text = ""

  if (frequency === "daily") {
    text = interval === 1 ? "Codziennie" : `Co ${interval} dni`
  } else if (frequency === "weekly") {
    const days = daysOfWeek.map(d => DAY_LABELS[d]).join(", ")
    text = interval === 1 ? `Co tydzień: ${days}` : `Co ${interval} tygodnie: ${days}`
  } else if (frequency === "monthly") {
    text = interval === 1 ? `Co miesiąc, ${dayOfMonth}. dnia` : `Co ${interval} miesiące, ${dayOfMonth}. dnia`
  } else if (frequency === "yearly") {
    text = interval === 1 ? "Co rok" : `Co ${interval} lat`
  }

  if (endDate) {
    text += ` do ${endDate}`
  }

  return text
}
